import { QueryClient } from '@tanstack/react-query';
import { useFlag, useUnleashContext } from '@unleash/proxy-client-react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { getSession } from 'next-auth/react';
import { useEffect } from 'react';

import SignupPage from '../../components/dynamicPages/SignupPage';
import MainLoader from '../../components/Loader';
import useSignUp from '../../hooks/useSignup';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../utils/react-query-utils';
import { provisionFlags } from '../../utils/ServerSideFeatureFlagUtils';

export default function Signup() {
  useUnleashContext();
  const router = useRouter();
  const { signUp } = useSignUp();
  const skipSignupForm = useFlag('signupBeforeSignupForm');

  useEffect(() => {
    if (skipSignupForm) {
      // When flag is enabled, skip the signup form and go directly to Azure B2C
      const callbackUrl = router.query.callbackUrl as string;
      const finalCallbackUrl = callbackUrl || '/lessons';

      //Empty request to be backwards compatible with old signup flow
      signUp({
        callbackUrl: finalCallbackUrl,
        authParams: {
          ui_locales: 'en',
          accountType: 'teacher',
          tenantName: '',
          schoolRole: '',
          subjects: '',
          grades: '',
          primaryUse: '',
          otherRole: '',
          otherSubjects: '',
          otherGrades: '',
          otherPrimaryUse: '',
          v3Properties: JSON.stringify({
            organizationId: undefined,
            organizationGeoLocation: {
              country: '',
              region: '',
              city: '',
            },
            intendedUses: '',
            signUpOrigin: '',
            otherIntendedUse: '',
            otherSignUpOrigin: '',
          }),
        },
      });
    }
  }, [skipSignupForm, signUp, router.query.callbackUrl]);

  // When flag is disabled, render the original signup page
  // This will collect form data and use the full signup flow with /signup/success page
  if (!skipSignupForm) {
    return <SignupPage />;
  }

  // When flag is enabled, show loading state while redirecting to Azure B2C
  return <MainLoader />;
}

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  const session = await getSession({ req: context.req });
  if (!session) {
    return {
      props: {
        ...(await detectAndBestEffortSetLanguage(context)),
      },
    };
  } else {
    const queryClient = new QueryClient();
    return {
      props: {
        ...(await provisionFlags(context, queryClient, session)),
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context)),
        session,
      },
    };
  }
};
